import dayjs from 'dayjs';
import 'dayjs/locale/es';

/** Valor utilizable: no nullish, no vacío y distinto del literal 'None' que envía la API */
const isValidValue = (value) => value != null && value !== '' && value !== 'None';
const cleanValue = (value) => {
    if (value === null || value === undefined) return value;
    return String(value)
        .trim()
        .replace(/\.+$/, '');   // elimina puntos finales (uno o varios)
};

/**
 * La API manda valores compuestos donde algun token puede venir como 'None'
 * (ej. "SIN CABELLO, None, CALVO COMPLETO"): se limpian uno a uno, no solo el valor completo.
 * @example
 * hasInfoField("SIN CABELLO, None, CALVO COMPLETO") // devuelve "SIN CABELLO, CALVO COMPLETO"
 * hasInfoField("None") // devuelve "SIN DATO"
 */
export const hasInfoField = (info) => {
    const clean = String(info ?? '')
        .split(',')
        .map(part => part.trim())
        .filter(part => part && part !== 'None')
        .join(', ');

    return clean || "SIN DATO";
};

/**
 * @example
 * verifyAge(25) // devuelve "25 AÑOS"
 * verifyAge(null) // devuelve "SIN DATO"
 * verifyAge("None") // devuelve "SIN DATO"
 */
export const verifyAge = (age) => age === null || age === undefined || age < 0 || age === 'None' ? 'SIN DATO' : `${age} AÑOS`;

/**
 * @example
 * verifyStature(1.75) // devuelve "1.75 m"
 * verifyStature(null) // devuelve "SIN DATO"
 * verifyStature("None") // devuelve "SIN DATO"
 */
export const verifyStature = (stature) => !stature || stature === 'None' ? 'SIN DATO' : `${stature} m`;

export const TAG_DATA = {
    "CON VIDA": {
        styles: { backgroundColor: '#1CA37A' },
        text: "LOCALIZADA\nCON VIDA"
    },
    "SIN VIDA": {
        styles: { backgroundColor: '#626262' },
        text: "LOCALIZADA\nSIN VIDA"
    },
    "NO APLICA": {
        styles: { backgroundColor: '#FE9523' },
        text: "PERSONA\nDESAPARECIDA"
    }
};

export const CONTACT_INFO = {
    PHONE: "33 3145 6314",
    EMAIL: "comisiondebusqueda@jalisco.gob.mx",
    SOCIAL_MEDIA: "@BusquedaJal"
};

/**
 * @example formatDate("2023-01-15");
 * // Devuelve "15 de enero de 2023"
 */
export const formatDate = (inputDate) => {
    if (inputDate === null || inputDate === undefined || inputDate === 'None') {
        return 'SIN DATO';
    }

    const date = dayjs(inputDate, 'YYYY-MM-DD').locale('es');
    if (!date.isValid()) {
        return 'SIN DATO';
    }

    return `${date.format('DD')} de ${date.format('MMMM')} de ${date.format('YYYY')}`;
};

// Sin información válida devuelve []; cada formato decide cómo mostrar "SIN DATOS".
export const processVestimenta = (vestimenta) => {
    if (!Array.isArray(vestimenta)) {
        return [];
    }

    return vestimenta
        .map(item => {
            const values = [
                item.prenda,
                item.marca,
                item.color,
                item.material,
                item.talla,
                item.tipo,
                item.descripcion
            ]
                .map(cleanValue)
                .filter(isValidValue)
                .join(' ');

            return {
                grupoPrenda: item.grupo_prenda,
                formattedText: values
            };
        })
        .filter(item => item.formattedText);
};

export const processSenasParticulares = (senaParticular) => {
    if (!Array.isArray(senaParticular)) {
        return [];
    }
    return senaParticular
        .map(item => ({
            tipoSena: item.tipo_sena,
            descripcion: item.descripcion,
            formattedText: [item.tipo_sena, item.descripcion]
                .map(cleanValue)
                .filter(isValidValue)
                .join(' ')
        }))
        .filter(item => item.formattedText);
};


const locationParts = (parts) => parts
    .map(cleanValue)
    .filter(isValidValue);

/**
 * Une las partes de una ubicación descartando las que no traen dato. Variadic porque el
 * rediseño pide "Colonia, Municipio, Estado" en una linea, y las llamadas de dos argumentos
 * que ya existen siguen funcionando igual.
 *
 * @param {...string} parts - de mayor a menor detalle (colonia, municipio, estado)
 * @returns {string} Las partes con dato unidas por ", ", o "SIN DATO" si no queda ninguna
 *
 * @example // Devuelve "Monterrey, Nuevo León"
 * formatLocation("Monterrey", "Nuevo León");
 *
 * @example // Devuelve "Cerro del Cuatro, San Pedro Tlaquepaque, Jalisco"
 * formatLocation("Cerro del Cuatro", "San Pedro Tlaquepaque", "Jalisco");
 *
 * @example // Devuelve "Guadalajara" — sin coma colgando
 * formatLocation("Guadalajara", "None");
 *
 * @example // Devuelve "SIN DATO" — nullish y 'None' con espacios tambien se descartan
 * formatLocation(null, " None ");
 */
export const formatLocation = (...parts) => {
    // Normaliza con los mismos helpers que vestimenta y señas: asi las cuatro salidas pueden
    // llamar a formatLocation a pelo, sin envolverla en hasInfoField para tapar los casos raros.
    const clean = locationParts(parts);

    return clean.length ? clean.join(', ') : "SIN DATO";
};
